/* <hero-slider> (sections/hero-slider.liquid): a small, dependency-free campaign slider.
   Markup contract: [data-slide] children (first one .is-active), optional [data-prev] [data-next] [data-go="i"] [data-toggle]
   [data-current] [data-total] [data-progress] (receives --p from 0 to 1).
   Attributes: data-autoplay, data-interval (ms), data-duration (ms).
   Later slides keep their image in data-srcset/data-src until they are about to be shown. */
(() => {
  if (customElements.get('hero-slider')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.count = this.slides.length;
      this.index = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
      this.interval = +this.dataset.interval || 6500;
      this.style.setProperty('--hs-duration', (+this.dataset.duration || 1100) + 'ms');
      this.wantsAutoplay = this.dataset.autoplay === 'true' && this.count > 1;
      this.holds = new Set();          // reasons autoplay is held: hover, focus, view, hidden, user, motion
      this.elapsed = 0;
      this.setAttribute('aria-roledescription', 'carousel');
      if (this.count < 2) { this.classList.add('is-single'); this.update(true); return; }
      this.classList.add('is-enhanced');

      this.slides.forEach((s, i) => {
        s.setAttribute('role', 'group');
        s.setAttribute('aria-roledescription', 'slide');
        s.setAttribute('aria-label', `${i + 1} / ${this.count}`);
      });
      this.querySelectorAll('[data-total]').forEach(n => (n.textContent = String(this.count).padStart(2, '0')));
      this.querySelectorAll('[data-prev]').forEach(b => b.addEventListener('click', () => this.go(this.index - 1, true)));
      this.querySelectorAll('[data-next]').forEach(b => b.addEventListener('click', () => this.go(this.index + 1, true)));
      this.querySelectorAll('[data-go]').forEach(b => b.addEventListener('click', () => this.go(+b.dataset.go, true)));
      this.toggle = this.querySelector('[data-toggle]');
      if (this.toggle) this.toggle.addEventListener('click', () => this.setUserPause(!this.holds.has('user')));

      this.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      });
      if (this.dataset.hoverPause !== 'false') this.addEventListener('pointerenter', e => e.pointerType === 'mouse' && this.hold('hover'));
      this.addEventListener('pointerleave', e => e.pointerType === 'mouse' && this.release('hover'));
      this.addEventListener('focusin', () => this.hold('focus'));
      this.addEventListener('focusout', e => { if (!this.contains(e.relatedTarget)) this.release('focus'); });
      this.bindSwipe();

      this.io = new IntersectionObserver(([en]) => (en.isIntersecting ? this.release('view') : this.hold('view')), { threshold: 0.25 });
      this.io.observe(this);
      this.onVis = () => (document.hidden ? this.hold('hidden') : this.release('hidden'));
      document.addEventListener('visibilitychange', this.onVis);
      this.onMotion = () => (reduce.matches ? this.hold('motion') : this.release('motion'));
      reduce.addEventListener?.('change', this.onMotion);
      this.onMotion();

      // Shopify theme editor: show the selected slide and stop the clock while editing.
      if (window.Shopify && Shopify.designMode) {
        this.hold('editor');
        document.addEventListener('shopify:block:select', this.onSelect = e => {
          const i = this.slides.indexOf(e.target.closest?.('[data-slide]'));
          if (i > -1) this.go(i, false);
        });
      }
      this.update(true);
      this.warm(this.index + 1);
      this.raf = requestAnimationFrame(t => this.tick(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVis);
      reduce.removeEventListener?.('change', this.onMotion);
      if (this.onSelect) document.removeEventListener('shopify:block:select', this.onSelect);
    }

    get running() { return this.wantsAutoplay && this.holds.size === 0; }
    hold(r) { this.holds.add(r); this.syncState(); }
    release(r) { this.holds.delete(r); this.syncState(); }
    setUserPause(on) { on ? this.hold('user') : this.release('user'); }
    syncState() {
      this.classList.toggle('is-paused', !this.running);
      if (this.toggle) {
        const paused = this.holds.has('user');
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay || 'Play slideshow' : this.toggle.dataset.labelPause || 'Pause slideshow');
      }
      // announce slide changes only when nothing moves by itself
      this.querySelector('[data-live]')?.setAttribute('aria-live', this.running ? 'off' : 'polite');
    }

    tick(t) {
      const dt = this.last ? Math.min(t - this.last, 100) : 0;
      this.last = t;
      if (this.running) {
        this.elapsed += dt;
        if (this.elapsed >= this.interval) this.go(this.index + 1, false);
      }
      const p = this.wantsAutoplay ? Math.min(this.elapsed / this.interval, 1) : 1;
      this.querySelectorAll('[data-progress], [data-go][aria-current="true"] [data-bar]').forEach(n => n.style.setProperty('--p', p.toFixed(4)));
      this.raf = requestAnimationFrame(n => this.tick(n));
    }

    warm(i) {
      const k = (i + this.count) % this.count;
      [this.slides[k], ...this.querySelectorAll(`[data-slide-media="${k}"]`)].forEach(s => s?.querySelectorAll('[data-srcset],[data-src]').forEach(n => {
        if (n.dataset.srcset) { n.srcset = n.dataset.srcset; delete n.dataset.srcset; }
        if (n.dataset.src) { n.src = n.dataset.src; delete n.dataset.src; }
      }));
    }

    go(i, byUser) {
      const next = (i + this.count) % this.count;
      if (next === this.index) return;
      this.warm(next);
      const prev = this.slides[this.index];
      const leaving = [prev, ...this.querySelectorAll(`[data-slide-media="${this.index}"]`)];
      this.querySelectorAll('.is-leaving').forEach(n => n.classList.remove('is-leaving'));
      leaving.forEach(n => { n.classList.remove('is-active'); n.classList.add('is-leaving'); });
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => leaving.forEach(n => n.classList.remove('is-leaving')), (+this.dataset.duration || 1100) + 50);
      this.dir = next > this.index || (this.index === this.count - 1 && next === 0) ? 1 : -1;
      this.index = next;
      this.elapsed = 0;
      this.update(false);
      this.warm(next + 1);
    }

    update(first) {
      this.style.setProperty('--hs-dir', this.dir || 1);
      this.slides.forEach((s, i) => {
        const on = i === this.index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        s.inert = !on;
      });
      const tone = this.slides[this.index]?.dataset.tone;
      if (tone && tone !== 'section') this.dataset.tone = tone; else delete this.dataset.tone;
      this.querySelectorAll('[data-slide-media]').forEach(n => n.classList.toggle('is-active', +n.dataset.slideMedia === this.index));
      this.querySelectorAll('[data-bar]').forEach(n => n.style.setProperty('--p', 0));
      this.querySelectorAll('[data-current]').forEach(n => (n.textContent = String(this.index + 1).padStart(2, '0')));
      this.querySelectorAll('[data-go]').forEach(b => b.setAttribute('aria-current', String(+b.dataset.go === this.index)));
      if (first) this.classList.add('is-ready');
    }

    bindSwipe() {
      let x0 = null, y0 = 0, id = null;
      this.addEventListener('pointerdown', e => {
        if (e.pointerType === 'mouse' || e.target.closest('a,button')) return;
        x0 = e.clientX; y0 = e.clientY; id = e.pointerId;
      });
      this.addEventListener('pointerup', e => {
        if (x0 === null || e.pointerId !== id) return;
        const dx = e.clientX - x0, dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) this.go(this.index + (dx < 0 ? 1 : -1), true);
      });
      this.addEventListener('pointercancel', () => (x0 = null));
    }
  }
  customElements.define('hero-slider', HeroSlider);
})();
